import type { ProductId, PurchaseResult, Store, StoreProduct, StoreTransaction } from "@highmarch/app";
import { PRODUCT_LISTINGS } from "./products";

/**
 * The web build's `Store` (FINISH_PLAN D9, ADR 0013): the tunnel play-tests buy gem packs for free. It never charges
 * and never talks to anything; its transactions are `environment: "sandbox"` and verified by construction. The debug
 * panel (ui/sandboxStoreSheet.ts, More → Sandbox store with `?debug`) steers it through every path a real store can
 * take, so the grant code is tested against them before StoreKit exists:
 * - `outcome` decides the next purchase: bought, cancelled, pending (Ask to Buy; approve or decline later), failed,
 *   or interrupted (paid, but the app "crashed" before the grant: the transaction comes back from `unfinished()`);
 * - `revoke` refunds a transaction, which arrives through `onTransaction` with `revokedAtMs`;
 * - `setCanPay(false)` is a device with purchases switched off (Screen Time).
 * Its ledger is kept in localStorage (`highmarch.sandbox-store`), so an unfinished transaction survives a reload like
 * StoreKit's.
 */

export type SandboxOutcome = "purchase" | "cancel" | "pending" | "fail" | "interrupt";
export const SANDBOX_OUTCOMES: readonly SandboxOutcome[] = ["purchase", "cancel", "pending", "fail", "interrupt"];

/** The ledger's storage (localStorage; tests pass a map). */
export interface SandboxStorage { getItem(key: string): string | null; setItem(key: string, value: string): void }

interface LedgerEntry { tx: StoreTransaction; finished: boolean }
interface Ledger { v: 1; nextId: number; entries: LedgerEntry[]; pending: ProductId[] }
const LEDGER_VERSION = 1;

export class SandboxStore implements Store {
  static readonly LedgerKey = "highmarch.sandbox-store";
  /** Ledger entries kept: enough for any play-test; the oldest finished ones go first. */
  static readonly MaxEntries = 200;
  readonly kind = "sandbox" as const;

  outcome: SandboxOutcome = "purchase";
  /** The storefront the sandbox reports (null: unknown, as a sandbox is); debug sets e.g. "BEL" to see random offers hide. */
  storefrontCode: string | null = null;
  private payAllowed = true;
  private readonly listeners: Array<(t: StoreTransaction) => void> = [];
  private readonly changeListeners: Array<() => void> = [];
  private ledger: Ledger;

  constructor(private readonly storage: SandboxStorage | null, private readonly nowMs: () => number = Date.now) {
    this.ledger = this.read();
  }

  async canPay(): Promise<boolean> { return this.payAllowed; }
  get paymentsAllowed(): boolean { return this.payAllowed; }
  setCanPay(allowed: boolean): void { this.payAllowed = allowed; this.changed(); }

  async products(ids: readonly ProductId[]): Promise<readonly StoreProduct[]> {
    return ids.flatMap((id) => {
      const p = PRODUCT_LISTINGS.find((l) => l.id === id);
      return p ? [{ id: p.id, kind: p.kind, displayName: p.displayName, displayPrice: `$${p.referencePriceUsd}` }] : [];
    });
  }

  async purchase(id: ProductId): Promise<PurchaseResult> {
    if (!PRODUCT_LISTINGS.some((l) => l.id === id)) return { status: "failed", reason: `unknown product ${id}` };
    if (!this.payAllowed) return { status: "failed", reason: "purchases are switched off on this device" };
    switch (this.outcome) {
      case "cancel": return { status: "cancelled" };
      case "fail": return { status: "failed", reason: "sandbox: simulated store failure" };
      case "pending":
        this.ledger.pending.push(id);
        this.write();
        return { status: "pending" };
      case "interrupt":
        this.issue(id);
        return { status: "failed", reason: "sandbox: interrupted before the grant (it comes back as unfinished)" };
      case "purchase":
        return { status: "purchased", transaction: this.issue(id) };
    }
  }

  async unfinished(): Promise<readonly StoreTransaction[]> {
    return this.ledger.entries.filter((e) => !e.finished).map((e) => e.tx);
  }

  async finish(transactionId: string): Promise<void> {
    const e = this.ledger.entries.find((x) => x.tx.transactionId === transactionId);
    if (!e || e.finished) return;
    e.finished = true;
    this.write();
  }

  /** The current entitlements: non-consumables bought and not refunded (every catalogue product is consumable today). */
  async restore(): Promise<readonly StoreTransaction[]> {
    return this.ledger.entries.filter((e) => e.tx.revokedAtMs === undefined && PRODUCT_LISTINGS.find((l) => l.id === e.tx.productId)?.kind === "nonConsumable").map((e) => e.tx);
  }

  async storefront(): Promise<string | null> { return this.storefrontCode; }

  onTransaction(listener: (t: StoreTransaction) => void): () => void {
    this.listeners.push(listener);
    return () => { const i = this.listeners.indexOf(listener); if (i >= 0) this.listeners.splice(i, 1); };
  }

  // ---------------------------------------------------------------- the debug panel's controls

  /** Purchases waiting on Ask to Buy. */
  get pendingProducts(): readonly ProductId[] { return this.ledger.pending; }
  /** Every transaction, newest first. */
  get transactions(): ReadonlyArray<{ readonly tx: StoreTransaction; readonly finished: boolean }> { return [...this.ledger.entries].reverse(); }
  /** The panel redraws on any change. */
  onChange(listener: () => void): () => void {
    this.changeListeners.push(listener);
    return () => { const i = this.changeListeners.indexOf(listener); if (i >= 0) this.changeListeners.splice(i, 1); };
  }
  setOutcome(o: SandboxOutcome): void { this.outcome = o; this.changed(); }

  /** Ask to Buy approved: the purchase arrives through `onTransaction`, as StoreKit's `Transaction.updates` does. */
  approvePending(): StoreTransaction | null {
    const id = this.ledger.pending.shift();
    if (id === undefined) return null;
    const tx = this.issue(id);
    this.emit(tx);
    return tx;
  }
  /** Ask to Buy declined: StoreKit says nothing, so neither does this. */
  declinePending(): void {
    if (this.ledger.pending.shift() !== undefined) this.write();
  }
  /** A refund: the transaction comes back through `onTransaction` with `revokedAtMs` set. */
  revoke(transactionId: string): StoreTransaction | null {
    const e = this.ledger.entries.find((x) => x.tx.transactionId === transactionId);
    if (!e || e.tx.revokedAtMs !== undefined) return null;
    e.tx = { ...e.tx, revokedAtMs: this.nowMs() };
    this.write();
    this.emit(e.tx);
    return e.tx;
  }
  /** Wipes the sandbox ledger (not the game's wallet). */
  reset(): void { this.ledger = emptyLedger(); this.write(); }

  // ---------------------------------------------------------------- ledger

  private issue(productId: ProductId): StoreTransaction {
    const n = this.ledger.nextId++;
    // The time as well as the counter: the ledger lives in localStorage, and after a reset (or WebKit clearing it while
    // IndexedDB keeps the save) a bare counter handed out ids the wallet already held, so the purchase granted nothing.
    const id = `sandbox-${this.nowMs().toString(36)}-${n}`;
    const tx: StoreTransaction = { transactionId: id, originalTransactionId: id, productId, quantity: 1, purchasedAtMs: this.nowMs(), environment: "sandbox" };
    this.ledger.entries.push({ tx, finished: false });
    while (this.ledger.entries.length > SandboxStore.MaxEntries) {
      const oldest = this.ledger.entries.findIndex((e) => e.finished);
      if (oldest < 0) break;
      this.ledger.entries.splice(oldest, 1);
    }
    this.write();
    return tx;
  }

  private emit(tx: StoreTransaction): void { for (const l of [...this.listeners]) l(tx); }
  private changed(): void { for (const l of [...this.changeListeners]) l(); }

  private read(): Ledger {
    try {
      const raw = this.storage?.getItem(SandboxStore.LedgerKey);
      if (raw) {
        const l = JSON.parse(raw) as Ledger;
        if (l.v === LEDGER_VERSION && Number.isInteger(l.nextId) && Array.isArray(l.entries) && Array.isArray(l.pending)) return l;
      }
    } catch { /* a bad ledger is a sandbox's to lose */ }
    return emptyLedger();
  }

  private write(): void {
    try { this.storage?.setItem(SandboxStore.LedgerKey, JSON.stringify(this.ledger)); } catch { /* private mode: the ledger lives in memory */ }
    this.changed();
  }
}

function emptyLedger(): Ledger { return { v: LEDGER_VERSION, nextId: 1, entries: [], pending: [] }; }
